/* eslint-disable global-require */
import Vue from 'vue';
import format from 'date-fns/format';
import { mapState } from 'vuex';
import Calendar from '../../classes/Calendar';

Vue.component('academy-lesson-component', {

    props: ['mainBookerName', 'mainBookerMemberId', 'locale', 'options'],

    data() {
        return {
            humanDate: '',
            calendar: new Calendar(),

            duration: '1',
            time: {
                startTime: null,
                proId: null,
            },
            pro: null,
            availablePros: [],
            anyAvailablePros: true,

            reservationId: '',
            visitId: '',

            visitConfirmed: false,
            playersConfirmed: false,
            finalConfirmation: false,

            playerCount: 1,
            players: [],

            lessonTypes: [],
            lessonTypeId: '',

            lessonContentTypes: [],
            lessonContentTypeId: null,
            lessonContentTypeName: '',
        };
    },

    watch: {
        date(val) {
            // Set human readable date.
            const dateObj = new Date(this.date);
            const locales = {
                en: require('date-fns/locale/en'),
                nl: require('date-fns/locale/nl'),
            };
            const localeFormats = {
                en: 'dddd MMMM DD',
                nl: 'dddd DD MMMM',
            };
            let localeObj = locales.en;
            let localeFormat = localeFormats.en;
            if (locales[window.appLocale]) {
                localeObj = locales[window.appLocale];
                localeFormat = localeFormats[window.appLocale];
            }
            this.humanDate = format(dateObj, localeFormat, { locale: localeObj });

            // Update time table.
            this.$api.gms.academy.findStartTimes(this.date, this.duration)
            .then((timesPerPro) => {
                this.availablePros = timesPerPro;
            })
            .catch((error) => {});
        },

        duration(val) {
            // Update time table if a date is set.
            if (this.date) {
                this.$api.gms.academy.findStartTimes(this.date, this.duration)
                .then((timesPerPro) => {
                    this.availablePros = timesPerPro;
                })
                .catch((error) => {});
            }
        },

        lessonContentType(val) {
            // Set human readable name on new id.
            for (let i = 0; i < this.lessonContentTypes.length; i++) {
                if (this.lessonContentTypes[i].id == val) {
                    this.lessonContentTypeName = this.lessonContentTypes[i].name;
                }
            }
        },

        availablePros(val) {
            // Set boolean if there are no pros available.
            this.anyAvailablePros = false;
            for (let i = 0; i < this.availablePros.length; i++) {
                if (this.availablePros[i].startTimes.length) {
                    this.anyAvailablePros = true;
                }
            }
        },
    },

    computed: {
        ...mapState('academy', {
            month: (state) => state.month,
            date: (state) => state.date,
            activeRound: (state) => state.activeRound,
        }),
    },

    mounted() {
        // Set lesson types.
        this.$api.gms.general.getLessonTypes()
        .then((response) => {
            this.lessonTypes = response.data.result;
        })
        .catch((error) => {});

        // Set lesson content types.
        this.$api.gms.general.getLessonContentTypes()
        .then((response) => {
            this.lessonContentTypes = response.data.result;
        })
        .catch((error) => {});

        // Set player 0 to the logged in user.
        this.setPlayer(0, this.mainBookerName, this.mainBookerMemberId);
    },

    methods: {

        /**
         * Sets player data at given index in the players property.
         *
         * @param {Number} playerIndex
         * @param {String} playerName
         * @param {String} playerId
         * @listens module: member-autocomplete~event: set-player
         */
        setPlayer(playerIndex, playerName, playerId) {
            this.players[playerIndex] = {
                playerName,
                playerId,
            };
        },

        /**
         * Fetches available times for the given day.
         *
         * @param {Object} day
         */
        selectDay(day) {
            this.calendar.selectDay(day);
            this.selectedDay = day;
            this.date = day.date;
        },

        /**
         * Create the initial temporary visit claim for this lesson & set the visit and reservation id's.
         *
         * @return {Void}
         */
        createClaim() {
            if (!this.time.startTime) {
                return;
            }

            this.submitting = true;

            this.$api.gms.academy.createAcademyClaim(postQs({
                date: this.date,
                startTime: this.time.startTime,
                duration: this.duration,
                proId: this.time.proId,
            }))
            .then((response) => {
                // Set id's
                this.reservationId = response.data.result.reservationId;
                this.visitId = response.data.result.visitId;

                this.submitting = false;
                this.visitConfirmed = true;
            })
            .catch((error) => {
                this.submitting = false;
            });
        },

        /**
         * Adds member ids to the claim, the number of players and the lesson type and lesson content type id's.
         *
         * @return {Void}
         */
        updateMemberClaim() {
            this.submitting = true;

            // Define lessonTypeId
            this.setLessonTypeId();

            // Create member id list
            const memberIds = this.createMemberIdList();

            // Make the call
            this.$api.gms.academy.updateAcademyLessonMemberClaim(postQs({
                reservationId: this.reservationId,
                numberOfMembers: this.playerCount,
                lessonTypeId: this.lessonTypeId,
                memberIds,
                contentTypeId: [this.lessonContentTypeId],
            }))
            .then((response) => {
                this.submitting = false;
                this.playersConfirmed = true;
            })
            .catch((error) => {
                this.submitting = false;
            });
        },

        /**
         * Loop players list until player count is reached and add the players that are set and have an id.
         *
         * @return {Array}
         */
        createMemberIdList() {
            const memberIds = [];
            for (let i = 0; i < this.playerCount; i++) {
                if (this.players[i] && this.players[i].playerId) {
                    memberIds.push(this.players[i].playerId);
                }
            }
            return memberIds;
        },

        /**
         * Set the lesson type id depending on the amount of players.
         *
         * @return {Void}
         */
        setLessonTypeId() {
            for (let i = this.lessonTypes.length - 1; i >= 0; i--) {
                if (this.playerCount <= this.lessonTypes[i].maxMembers) {
                    this.lessonTypeId = this.lessonTypes[i].id;
                }
            }
        },

        /**
         * Makes the visit claim definitive.
         *
         * @return {Void}
         */
        makeDefinitive() {
            this.submitting = true;

            this.$api.gms.academy.makeVisitDefinitive(this.visitId)
            .then((response) => {
                this.submitting = false;
                this.finalConfirmation = true;
            })
            .catch((error) => {
                this.submitting = false;
            });
        },

        /**
         * Handle clicking on a time slot of a golf pro.
         *
         * @param  {String} startTime
         * @param  {String} proId
         * @return {Void}
         */
        onTimeslotClick(startTime, pro) {
            this.pro = pro;
            this.time = {
                startTime,
                proId: this.pro.id,
            };
        },

        /**
         * Handle clicking the confirm button on page 1.
         *
         * @return {Void}
         */
        onTimeslotConfirmClick() {
            if (!this.submitting) {
                this.createClaim();
            }
        },

        /**
         * Handle clicking the next button on page 2.
         *
         * @return {Void}
         */
        onPlayersConfirmClick() {
            if (!this.submitting) {
                this.updateMemberClaim();
            }
        },

        /**
         * Handle clicking on the final confirmation button on page 3.
         *
         * @return {Void}
         */
        onFinalConfirmationClick() {
            if (!this.submitting) {
                this.makeDefinitive();
            }
        },
    },
});
